import React, { useCallback, useEffect, useRef, useState } from "react";
import { Row, Col, Button, Image } from "react-bootstrap";
import { FaTrashAlt } from "react-icons/fa";
import { BiLoaderCircle } from "react-icons/bi";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import { ShimmerCategoryItem } from "react-shimmer-effects";

import "./Favorites.css";

import { getWatchListing, deleteFavorite } from "@/api/favorites";
import DashboardSectionWrapper from "../components/DashboardSectionWrapper";
import DeletePopupModal from "../components/delete-modal/DeletePopupModal";
import { trackApiCall, trackButtonClick } from "@/utils/analytics";

import { getPropertyURL } from "@/utils/propertyNavigation";
import { markInternalNavigation } from "@/utils/attributionCapture";
import imageOnTheWay from "../../../../public/assets/images/imageOnTheWay.webp";

const Favorites = () => {
  const navigate = useNavigate();
  const observerRef = useRef();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [isDeleteModalActive, setIsDeleteModalActive] = useState(false);
  const [deletingPropertyId, setDeletingPropertyId] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [watchCount, setWatchCount] = useState(null);

  const fetchWatchList = useCallback(async (page = 0, append = false) => {
    try {
      if (page === 0) setLoading(true);
      else setLoadingMore(true);

      const response = await getWatchListing(page);
      const newData = response.watchListResponse || [];

      if (append) {
        setData((prev) => [...prev, ...newData]);
      } else {
        setData(newData);
      }

      setWatchCount(response.watchCount || 0);
      setCurrentPage(page);
    } catch (error) {
      console.error("Error fetching watchlist:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  const lastPropertyElementRef = useCallback(
    (node) => {
      if (loading || loadingMore || watchCount === 0) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && watchCount > 0 && !loadingMore) {
            fetchWatchList(currentPage + 1, true);
          }
        },
        { rootMargin: "200px" }
      );

      if (node) observerRef.current.observe(node);
    },
    [loading, loadingMore, watchCount, currentPage, fetchWatchList]
  );

  useEffect(() => {
    fetchWatchList();
  }, [fetchWatchList]);

  const handleFavPropertyDelete = async (item) => {
    try {
      setDeletingPropertyId(item.propertyId);

      trackButtonClick({
        eventName: "delete_conf_yes_click",
        eventCategory: "Watched-Properties",
        eventLabel: item.propertyId,
        eventSource: "on delete popup yes click",
      });

      // Delete the favorite
      const result = await deleteFavorite(item.propertyId);
      if (result.response === "SUCCESS") {
        setIsDeleteModalActive(false);
        setSelectedProperty(null);

        trackApiCall({
          eventName: "delete_success",
          eventCategory: "Watched-Properties",
          eventLabel: item.propertyId,
          eventSource: "on delete popup yes click",
        });

        // Fetch updated data after successful deletion
        await fetchWatchList();

        // const emailId = "";
        // const pageNumber = item.pageNum ? item.pageNum : 0;
        // const pageSize = 3;
        // getUserSavedProperties(emailId, pageNumber, pageSize);

        toast.success("Property removed from favorites");
      } else {
        toast.error("Failed to remove property from favorites");

        trackApiCall({
          eventName: "delete_failure",
          eventCategory: "Watched-Properties",
          eventLabel: item.propertyId,
          eventSource: "on delete popup yes click",
        });
      }
    } catch (error) {
      console.error("Error removing property from favorites:", error);
      toast.error("Failed to remove property from favorites");
    } finally {
      setDeletingPropertyId(null);
    }
  };

  // Function to format address
  const getFormattedAddress = (propertyAddress) => {
    if (!propertyAddress) return "N/A";
    const { streetNumber, streetName, city, state, zip } = propertyAddress;
    return `${streetNumber || ""} ${streetName || ""}, ${city || ""}, ${
      state || ""
    } ${zip || ""}`.trim();
  };

  // Function to calculate time left
  const calculateTimeLeft = (endDate) => {
    if (!endDate) return "NA";
    const end = new Date(endDate);
    const now = new Date();
    const diff = end - now;

    if (diff <= 0) return "Ended";

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    return `${days}d ${hours}h left`;
  };

  // Function to format currency
  const formatCurrency = (amount) => {
    if (!amount) return "NA";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Helper function to format time display with leading zeros
  const formatTimeDisplay = (timeInfo) => {
    if (!timeInfo) return "NA";

    const formatWithLeadingZero = (value) => {
      return value.length === 1 ? `0${value}` : value;
    };

    const days = formatWithLeadingZero(timeInfo.days);
    const hours = formatWithLeadingZero(timeInfo.hrs);
    const minutes = formatWithLeadingZero(timeInfo.mins);

    return `${days}:${hours}:${minutes}`;
  };

  return (
    <DashboardSectionWrapper title="Favorites">
      <Row className="favourites-main-container">
        <>
          {loading ? (
            <div className="p-4">
              {[...Array(3)].map((_, index) => (
                <ShimmerCategoryItem
                  hasImage
                  imageType="thumbnail"
                  imageWidth={160}
                  imageHeight={120}
                  text
                  cta
                />
              ))}
            </div>
          ) : data?.length > 0 ? (
            <>
              <div className="d-none d-sm-block">
                <Row className="favourites-container">
                  <Col
                    xs={4}
                    md={4}
                    className="favourites-container-child1"
                    tabIndex="0"
                  >
                    <strong className="pl10">Property</strong>
                  </Col>
                  <Col
                    xs={6}
                    md={4}
                    className="favourites-container-child2"
                    tabIndex="0"
                  >
                    <strong>Details</strong>
                  </Col>
                </Row>
              </div>

              {data.map((item, index) => (
                <Row
                  className="mt-3 fav-tupple-container"
                  key={item.propertyId}
                  ref={
                    index === data.length - 1 ? lastPropertyElementRef : null
                  }
                >
                  {/* property image */}
                  <Col xs={12} md={4} className="fav-img-container">
                    <Image
                      className="fav-image-dimension"
                      tabIndex="0"
                      style={{ cursor: "pointer" }}
                      alt="image on the way"
                      src={
                        item?.propertyImageDetails?.imageStoragePath ||
                        imageOnTheWay
                      }
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = imageOnTheWay;
                      }}
                      onClick={() => {
                        markInternalNavigation(); navigate(getPropertyURL(`/property/${item?.propertyId}`, item?.propertyCategory, item?.propertyStatus));
                        trackButtonClick({
                          eventName: "property_image_click",
                          eventCategory: "Watched-Properties",
                          eventLabel: `${item.propertyId}`,
                        });
                      }}
                      rounded
                      fluid
                    />
                  </Col>

                  {/* property details middle */}
                  <Col xs={12} md={5} className="px-0 px-sm-3">
                    <div className="fav-property fav-property-details">
                      {/* Full Address */}
                      <a href={getPropertyURL(`/property/${item?.propertyId}`, item?.propertyCategory, item?.propertyStatus)}>
                        <p
                          className="mb-0 py-1"
                          style={{
                            cursor: "pointer",
                            color: "#000",
                            textDecoration: "none",
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            trackButtonClick({
                              eventName: "address_link_click",
                              eventCategory: "Watched-Properties",
                              eventLabel: `${item.propertyId}`,
                            });
                            markInternalNavigation(); navigate(getPropertyURL(`/property/${item?.propertyId}`, item?.propertyCategory, item?.propertyStatus));
                          }}
                        >
                          <b>{getFormattedAddress(item.propertyAddress)}</b>
                        </p>
                      </a>

                      {item.propertyStatus !== "CLEAR_TO_SALE" &&
                        item.propertyStatus !== "SCHEDULED" && (
                          <>
                            {item.listingDetail.listingType === "TRNL" ? (
                              <p className="mb-0" tabIndex="0">
                                Traditional Sale
                              </p>
                            ) : item.listingDetail.listingEndingInfo ===
                              null ? (
                              <p className="mb-0" tabIndex="0">
                                Auction Ended
                              </p>
                            ) : (
                              <p className="mb-0" tabIndex="0">
                                Time Left:{" "}
                                {formatTimeDisplay(
                                  item.listingDetail.listingEndingInfo
                                )}
                              </p>
                            )}

                            {/* Starting Bid or listing price */}
                            {item?.listingDetail?.currentHighestPrice !==
                              null &&
                            item?.listingDetail?.currentHighestPrice !== 0 ? (
                              <p className="mb-0" tabIndex="0">
                                Current Highest Bid:{" "}
                                {formatCurrency(
                                  item?.listingDetail?.currentHighestPrice
                                )}
                              </p>
                            ) : item.propertyStatus === "Waiting For Listing" ||
                              item.propertyStatus === "Active" ? (
                              <p className="mb-0" tabIndex="0">
                                Listing Price:{" "}
                                {formatCurrency(item?.listingDetail?.listPrice)}
                              </p>
                            ) : (
                              <p className="mb-0" tabIndex="0">
                                Starting Bid:{" "}
                                {formatCurrency(
                                  item?.listingDetail?.startingPrice
                                )}
                              </p>
                            )}

                            {/* Status */}
                            <p className="mb-0" tabIndex="0">
                              Status: {item.propertyStatus || "NA"}
                            </p>
                          </>
                        )}

                      {item.propertyStatus === "SCHEDULED" && (
                        <p className="mb-0 fav-fc-title" tabIndex="0">
                          Foreclosure sale
                        </p>
                      )}
                    </div>
                  </Col>

                  {/* property buttons */}
                  {item.propertyStatus === "CLEAR_TO_SALE" ? (
                    <Col
                      xs={12}
                      md={2}
                      className="fav-prop-btn-container1 cleared-to-sell"
                      tabIndex="0"
                    >
                      <span>Cleared to Sell</span>
                    </Col>
                  ) : item.propertyStatus === "SCHEDULED" ? (
                    <Col
                      xs={12}
                      md={2}
                      className="fav-prop-btn-container1 cleared-to-sell"
                      tabIndex="0"
                    >
                      <span>Scheduled</span>
                    </Col>
                  ) : (
                    <Col xs={12} md={2} className="fav-prop-btn-container1">
                      <Button
                        aria-label="view details"
                        className="fav-property-details-btn"
                        href={getPropertyURL(`/property/${item?.propertyId}`, item?.propertyCategory, item?.propertyStatus)}
                        onClick={(e) => {
                          e.preventDefault();
                          trackButtonClick({
                            eventName: "view_details_button_click",
                            eventCategory: "Watched-Properties",
                            eventLabel: `${item.propertyId}`,
                          });
                          markInternalNavigation(); navigate(getPropertyURL(`/property/${item?.propertyId}`, item?.propertyCategory, item?.propertyStatus));
                        }}
                      >
                        View Details
                      </Button>
                    </Col>
                  )}
                  <Col xs={12} md={1} className="fav-prop-btn-container2">
                    <Button
                      className="fav-property-dlt-btn"
                      aria-label="delete"
                      onClick={() => {
                        setSelectedProperty(item);
                        setIsDeleteModalActive(true);
                        trackButtonClick({
                          eventName: "delete_image_click",
                          eventCategory: "Watched-Properties",
                          eventLabel: `${item.propertyId}`,
                        });
                      }}
                      disabled={deletingPropertyId === item.propertyId}
                    >
                      {deletingPropertyId === item.propertyId ? (
                        <BiLoaderCircle />
                      ) : (
                        <FaTrashAlt />
                      )}
                    </Button>
                  </Col>
                </Row>
              ))}

              {loadingMore && (
                <div className="p-4 text-center">
                  <BiLoaderCircle className="spinner" />
                </div>
              )}
            </>
          ) : (
            <div className="noResultContainer">
              <p>
                Add properties you like for easy access and real-time updates on
                all your favorite listings.
              </p>
            </div>
          )}
          <DeletePopupModal
            title="Confirm Delete"
            subtitle="Are you sure you want to delete?"
            isLoading={deletingPropertyId}
            isOpen={isDeleteModalActive}
            onClose={() => {
              setIsDeleteModalActive(false);
              trackButtonClick({
                eventName: "delete_conf_no_click",
                eventCategory: "Watched-Properties",
                eventLabel: selectedProperty?.propertyId,
              });
            }}
            onConfirm={() => handleFavPropertyDelete(selectedProperty)}
          />
        </>
      </Row>
    </DashboardSectionWrapper>
  );
};

export default Favorites;
